// The NC1 backup-code file: what RecoveryCodeDialog and EncryptionSetupWizard
// download and what RecoveryCodeFileButton reads back. The name, the text and
// the parse live here so the three stay in step.
//
// The file names the account it belongs to (2026-10-10): a household with
// several encrypted accounts ends up with several of these in one Downloads
// folder, and a code only unlocks the identity it was minted for. The email
// goes into the file NAME (findable in the picker) and into an `Account:`
// line of the TEXT (survives a rename); the reader compares the text's line
// with the account being unlocked and warns before trying someone else's
// file. A file without the line — an older download, a hand-made file — is
// simply not checked.
//
// The file is also dated (2026-10-10): a new backup code replaces the old one
// the moment it is minted, so an account that has had several ends up with
// several files and only the newest works. The stamp is WHEN THE CODE WAS
// MINTED — every mint site records Date.now() beside generateRecoveryCode and
// hands it to the dialog — not when the file was saved: "Download" can be
// pressed any time the dialog is open, and a code may be downloaded twice.
// It goes into the name (`-2026-10-10-1432`, local time, so the picker sorts
// one account's files by date) and into a `Created:` line of the text (local
// time plus the UTC offset, so the instant is unambiguous). No mint time
// known = no stamp, never "now".

import { extractRecoveryCode } from './RecoveryCodeInput.js'

const FILE_INFIX = '-recovery-code'
const FILE_EXT = '.txt'
const ACCOUNT_LABEL = 'Account:'
const CREATED_LABEL = 'Created:'

export function brandName () {
  return process.env.VUE_APP_NAME || 'account'
}

function cleanEmail (email) {
  return String(email || '').trim().toLowerCase()
}

// ms since the epoch or a Date → Date, or null when unknown / unusable.
// 0 counts as unknown: no code was minted in 1970, and it lets the hosts keep
// a plain numeric field.
function mintDate (mintedAt) {
  if (!mintedAt) return null
  const d = mintedAt instanceof Date ? mintedAt : new Date(mintedAt)
  return Number.isFinite(d.getTime()) ? d : null
}

const two = (n) => String(n).padStart(2, '0')

/** `2026-10-10-1432`: the mint moment in local time, as a file-name suffix. '' when unknown. */
export function recoveryFileStamp (mintedAt) {
  const d = mintDate(mintedAt)
  if (!d) return ''
  return `${d.getFullYear()}-${two(d.getMonth() + 1)}-${two(d.getDate())}-${two(d.getHours())}${two(d.getMinutes())}`
}

/** `2026-10-10 14:32 (UTC-06:00)`: the mint moment for the `Created:` line. '' when unknown. */
export function recoveryCreatedStamp (mintedAt) {
  const d = mintDate(mintedAt)
  if (!d) return ''
  const east = -d.getTimezoneOffset() // minutes east of UTC at that instant
  const abs = Math.abs(east)
  const offset = `UTC${east < 0 ? '-' : '+'}${two(Math.floor(abs / 60))}:${two(abs % 60)}`
  return `${d.getFullYear()}-${two(d.getMonth() + 1)}-${two(d.getDate())} ${two(d.getHours())}:${two(d.getMinutes())} (${offset})`
}

/**
 * `<brand>-recovery-code-<email>`, or `<brand>-recovery-code` when no email is
 * known: what every file this app saved for the account starts with, whatever
 * its date. Everything a file system might refuse becomes `_`; `@` stays —
 * every OS accepts it and it keeps the name readable.
 */
export function recoveryFileNamePrefix (email, brand = brandName()) {
  const safe = cleanEmail(email).replace(/[^0-9a-z@._+-]/g, '_')
  return `${brand}${FILE_INFIX}${safe ? '-' + safe : ''}`
}

/**
 * `<brand>-recovery-code-<email>-<YYYY-MM-DD-HHMM>.txt`; the email and the
 * stamp are each left out when unknown.
 */
export function recoveryFileName (email, { brand = brandName(), mintedAt = null } = {}) {
  const stamp = recoveryFileStamp(mintedAt)
  return `${recoveryFileNamePrefix(email, brand)}${stamp ? '-' + stamp : ''}${FILE_EXT}`
}

/**
 * The file body: a header, the `Account:` and `Created:` lines (each only
 * when known), the code on its own line (extractRecoveryCode finds it by
 * shape — the words around it are free) and the warning. Also the body of
 * the "Email" button's mailto.
 */
export function recoveryFileText ({ code, email, brand = brandName(), mintedAt = null }) {
  const who = cleanEmail(email)
  const created = recoveryCreatedStamp(mintedAt)
  const facts = []
  if (who) facts.push(`${ACCOUNT_LABEL} ${who}`)
  if (created) facts.push(`${CREATED_LABEL} ${created}`)
  const factLines = facts.length ? facts.join('\n') + '\n\n' : ''
  const newest = created
    ? ' A new backup code replaces this one, so if you have more than one of these files, keep the one with the latest Created date.'
    : ''
  return `${brand} end-to-end encryption backup code\n\n${factLines}${code}\n\nKeep this somewhere safe. Anyone with this code can unlock your encrypted data. If you lose it, you can reset encryption from your Account page.${newest}\n`
}

/**
 * Read a backup code file back: `{ code, email }`. `code` is the dash-grouped
 * code or null when the text holds none; `email` is the lowercased address
 * of the file's `Account:` line, or null when there is no such line.
 */
export function parseRecoveryFile (text) {
  const source = String(text || '')
  const match = source.match(/^[ \t]*account[ \t]*:[ \t]*(\S+@\S+)[ \t]*$/im)
  return {
    code: extractRecoveryCode(source),
    email: match ? match[1].toLowerCase() : null
  }
}

/** True when `name` is shaped like a file this app saved, with or without an email or date, any brand. */
export function looksLikeRecoveryFileName (name) {
  const lower = String(name || '').toLowerCase()
  return lower.includes(FILE_INFIX) && lower.endsWith(FILE_EXT)
}
